import React, { useState, useEffect } from "react";
import { Row, Col, Card, Carousel, Button, Modal } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getWaterSports } from "../store/slices/watersports";

import Navbarsticky from "./NavbarSticky";
import Footer from "./Footer";
import HTMLRenderer from "../Component/htmlrender";

import "../watersports.css";

export default function WaterSportsDetails() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split("T")[0];

  const [bookingDate, setBookingDate] = useState("");
  const [paxError, setPaxError] = useState("");

  const placesRef = React.useRef(null);

  const [videos, setVideos] = useState([]);
  const [packageId, setPackageId] = useState("");
  const [packageName, setPackageName] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);
  const [places, setPlaces] = useState([]);

  /* ---------------- BOOKING STATE ---------------- */
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [adultCount, setAdultCount] = useState(1);
  const [childCount, setChildCount] = useState(0);

  /* ---------------- UI STATE ---------------- */
  const [activeIndex, setActiveIndex] = useState(0);

  /* ---------------- FETCH DATA ---------------- */

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await dispatch(getWaterSports({ id })).unwrap();
        const data = res?.waterSports?.WaterSportsData?.[0];
        if (!data) return;

        setPackageId(data._id);
        setPackageName(data.name);
        setDescription(data.description);
        setImages(data.image || []);
        setPlaces(data.place || []);
        setVideos(data.videos || []);
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, [dispatch, id]);

  useEffect(() => {
    if (!selectedPlace) return;

    const totalPax = adultCount + childCount;
    const applicablePricing = getApplicablePricing(selectedPlace, totalPax);

    if (!applicablePricing) {
      const message = getPaxValidationMessage(selectedPlace, totalPax);

      //  Only alert if error changed
      if (paxError !== message) {
        alert(message);
      }

      setPaxError(message);
      setSelectedTime("");
    } else {
      setPaxError("");
    }
  }, [adultCount, childCount, selectedPlace]);

  const getPaxValidationMessage = (placeObj, totalPax) => {
    if (!placeObj?.pricing?.length) return "Pricing not available.";

    const sortedPricing = [...placeObj.pricing].sort(
      (a, b) => Number(a.minPax) - Number(b.minPax),
    );

    const minAllowed = Number(sortedPricing[0].minPax) || 0;

    const lastSlab = sortedPricing[sortedPricing.length - 1];
    const maxAllowed = lastSlab.maxPax ? Number(lastSlab.maxPax) : Infinity;

    if (totalPax < minAllowed) {
      return `Minimum ${minAllowed} pax required.`;
    }

    if (maxAllowed !== Infinity && totalPax > maxAllowed) {
      return `Maximum ${maxAllowed} pax allowed.`;
    }

    return "Selected pax not allowed for this location.";
  };

  const getApplicablePricing = (placeObj, totalPax) => {
    if (!placeObj?.pricing?.length) return null;

    return (
      placeObj.pricing.find((p) => {
        const min = Number(p.minPax) || 0;
        const max = p.maxPax ? Number(p.maxPax) : Infinity;

        return totalPax >= min && totalPax <= max;
      }) || null
    );
  };

  /* ---------------- PRICE LOGIC ---------------- */

  const cheapestPrice = places.length
    ? Math.min(
        ...places.flatMap((p) =>
          p.pricing?.length ? p.pricing.map((pr) => pr.price) : [],
        ),
      )
    : null;

  const handleBookNow = () => {
    if (!bookingDate) {
      alert("Please select booking date");
      return;
    }

    if (!selectedPlace || !selectedTime) {
      alert("Please select location and time slot");
      return;
    }

    if (adultCount <= 0) {
      alert("At least one adult is required");
      return;
    }

    const totalParticipants = adultCount + childCount;

    const applicablePricing = getApplicablePricing(
      selectedPlace,
      totalParticipants,
    );

    if (!applicablePricing) {
      const message = getPaxValidationMessage(selectedPlace, totalParticipants);
      alert(message);
      return;
    }

    let totalAmount = 0;

    if (applicablePricing.type === "PER_PERSON") {
      totalAmount = applicablePricing.price * totalParticipants;
    } else {
      totalAmount = applicablePricing.price;
    }

    navigate("/checkout", {
      state: {
        bookingType: "WATER_SPORT",
        waterSportId: packageId,
        travelDate: bookingDate,
        pax: {
          adults: adultCount,
          children: childCount,
          infants: 0,
        },
        pricing: {
          basePrice: applicablePricing.price,
          taxAmount: 0,
          discount: 0,
          amountPayable: totalAmount,
        },
        meta: {
          waterSportName: packageName,
          placeName: selectedPlace.placeName,
          timeSlot: selectedTime,
        },
      },
    });
  };

  return (
    <>
      <Navbarsticky />

      <section className="tour-hero" />

      <div className="top-booking-strip mt-5">
        <div className="container">
          <h3 className="fw-bold">{packageName}</h3>
        </div>
      </div>

      <div className="container py-4">
        <Row>
          {/* LEFT */}
          <Col md={8}>
            <Card className="border-0 shadow-sm p-3 mb-4">
              <Carousel
                activeIndex={activeIndex}
                onSelect={setActiveIndex}
                indicators={false}
              >
                {images.map((img, i) => (
                  <Carousel.Item key={i}>
                    <img
                      src={img.mediaLink}
                      className="w-100 rounded-4"
                      style={{ height: 420, objectFit: "cover" }}
                      alt=""
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
            </Card>

            <Card className="border-0 shadow-sm p-4 mb-4">
              <h4 className="fw-bold mb-3">Overview</h4>
              <HTMLRenderer htmlContent={description} />
            </Card>
          </Col>

          {/* RIGHT */}
          <Col md={4}>
            <Card className="border-0 shadow-sm p-3 mb-4">
              <h5 className="fw-bold mb-3">Select Date & Pax</h5>

              {/* DATE */}
              <div className="mb-3">
                <label className="fw-semibold mb-1">Booking Date</label>
                <input
                  type="date"
                  className="form-control"
                  min={minDate}
                  value={bookingDate}
                  onChange={(e) => {
                    setBookingDate(e.target.value);
                    setSelectedPlace(null);
                    setSelectedTime("");
                  }}
                />
              </div>

              {/* PAX */}
              <Row>
                <Col>
                  <label className="fw-semibold mb-1">Adults</label>
                  <input
                    type="text"
                    className="form-control"
                    value={adultCount}
                    onChange={(e) => {
                      let value = e.target.value;
                      value = value.replace(/\D/g, "");
                      if (value.length > 2) value = value.slice(0, 2);
                      const num = Math.min(Number(value || 0), 99);
                      setAdultCount(num);
                    }}
                  />
                </Col>
                <Col>
                  <label className="fw-semibold mb-1">Children</label>
                  <input
                    type="text"
                    className="form-control"
                    value={childCount}
                    onChange={(e) => {
                      let value = e.target.value;
                      value = value.replace(/\D/g, "");
                      if (value.length > 2) value = value.slice(0, 2);
                      const num = Math.min(Number(value || 0), 20);
                      setChildCount(value === "" ? "" : num);
                    }}
                  />
                </Col>
              </Row>
            </Card>
            <h4 ref={placesRef} className="fw-bold mb-4">
              Select Location & Time
            </h4>

            {!bookingDate ? (
              <div className="text-muted">
                Please select date & pax to continue
              </div>
            ) : (
              places.map((p, i) => {
                const isSelected = selectedPlace?.placeName === p.placeName;

                return (
                  <div
                    key={i}
                    className={`booking-card mb-4 ${isSelected ? "active" : ""}`}
                  >
                    {/* HEADER */}
                    <div className="booking-header">
                      <h5 className="booking-title">{p.placeName}</h5>
                      {(() => {
                        const placeMinPrice = p.pricing?.length
                          ? Math.min(...p.pricing.map((pr) => pr.price))
                          : null;

                        return placeMinPrice === cheapestPrice ? (
                          <span className="best-value-pill">Best Value</span>
                        ) : null;
                      })()}
                    </div>

                    {/* PRICE */}
                    <div className="price-pill">
                      {(() => {
                        const totalPax = adultCount + childCount;
                        const dynamicPricing = getApplicablePricing(
                          p,
                          totalPax,
                        );

                        if (dynamicPricing) {
                          return (
                            <>
                              ₹ {dynamicPricing.price}
                              <span>
                                {dynamicPricing.type === "PER_PERSON"
                                  ? "/Person"
                                  : "/Total"}
                              </span>
                            </>
                          );
                        }

                        // 🚨 INVALID PAX → SHOW ERROR
                        const message = getPaxValidationMessage(p, totalPax);

                        return (
                          <span style={{ color: "white" }}>{message}</span>
                        );
                      })()}
                    </div>

                    {/* TIME SLOTS */}
                    <div className="time-slot-wrapper">
                      {p.timings.map((t, idx) => (
                        <button
                          key={idx}
                          className={`time-pill ${
                            isSelected && selectedTime === t ? "active" : ""
                          }`}
                          onClick={() => {
                            const totalPax = adultCount + childCount;
                            const applicablePricing = getApplicablePricing(
                              p,
                              totalPax,
                            );

                            if (!applicablePricing) {
                              const message = getPaxValidationMessage(
                                p,
                                totalPax,
                              );
                              alert(message);
                              return;
                            }

                            setSelectedPlace(p);
                            setSelectedTime(t);
                          }}
                        >
                          {t}
                        </button>
                      ))}
                    </div>

                    {/* BOOK NOW */}
                    {isSelected && (
                      <Button
                        className="book-now-btn mt-3 w-100"
                        onClick={handleBookNow}
                      >
                        Book Now
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </Col>
        </Row>
      </div>

      <Footer />
    </>
  );
}
